import type { ApiMessage } from '../../api/types';
import { MAIN_IDB_STORE } from '../../util/browser/idb';

import type { ArchivedMessageEntry, MessageArchiveDatabase } from './types';
import {
  archiveEntryKey,
  EMPTY_MESSAGE_ARCHIVE,
  MESSAGE_ARCHIVE_DB_KEY,
} from './types';

let memoryDb: MessageArchiveDatabase = { ...EMPTY_MESSAGE_ARCHIVE };
let loadPromise: Promise<void> | undefined;

function cloneMessage(message: ApiMessage): ApiMessage {
  return JSON.parse(JSON.stringify(message)) as ApiMessage;
}

export async function loadMessageArchive(): Promise<MessageArchiveDatabase> {
  if (!loadPromise) {
    loadPromise = (async () => {
      const stored = await MAIN_IDB_STORE.get<MessageArchiveDatabase>(MESSAGE_ARCHIVE_DB_KEY);
      if (stored?.version) {
        memoryDb = {
          ...EMPTY_MESSAGE_ARCHIVE,
          ...stored,
          entries: stored.entries || {},
        };
      }
    })();
  }
  await loadPromise;
  return memoryDb;
}

export function getMessageArchive(): MessageArchiveDatabase {
  return memoryDb;
}

let saveTimer: number | undefined;

function scheduleArchiveSave() {
  if (typeof window === 'undefined') return;
  if (saveTimer) {
    window.clearTimeout(saveTimer);
  }
  saveTimer = window.setTimeout(() => {
    saveTimer = undefined;
    void MAIN_IDB_STORE.set(MESSAGE_ARCHIVE_DB_KEY, memoryDb);
  }, 600);
}

export function upsertArchiveEntry(entry: ArchivedMessageEntry) {
  const key = archiveEntryKey(entry.chatId, entry.messageId);
  memoryDb.entries[key] = entry;
  scheduleArchiveSave();
}

export function archiveMessageSnapshot(
  message: ApiMessage,
  patch?: Partial<Pick<ArchivedMessageEntry, 'editHistory' | 'isDeleted'>>,
) {
  const key = archiveEntryKey(message.chatId, message.id);
  const existing = memoryDb.entries[key];
  const snapshot = cloneMessage(message);

  upsertArchiveEntry({
    chatId: message.chatId,
    messageId: message.id,
    snapshot,
    editHistory: patch?.editHistory ?? existing?.editHistory ?? message.editHistory,
    isDeleted: patch?.isDeleted ?? existing?.isDeleted,
    updatedAt: Date.now(),
  });
}

export function getArchiveEntry(chatId: string, messageId: number) {
  return memoryDb.entries[archiveEntryKey(chatId, messageId)];
}

export function getArchivedDeletedForChat(chatId: string): ArchivedMessageEntry[] {
  return Object.values(memoryDb.entries).filter(
    (entry) => entry.chatId === chatId && entry.isDeleted,
  );
}
